import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MarketplaceListing, SUBSCRIPTION_PLANS } from '../types';
import { marketplaceApi, productsPublicApi, personalMarketplaceApi } from '../services/api';

const CATEGORIES = [
  'All Categories',
  'Electronics',
  'Fashion & Clothing',
  'Home & Garden',
  'Sports & Outdoors',
  'Vehicles',
  'Collectibles',
  'Books & Media',
  'Toys & Games',
  'Arts & Crafts',
  'Services',
  'Other'
];

const CONDITIONS = {
  new: { label: 'New', color: '#28a745' },
  like_new: { label: 'Like New', color: '#20c997' },
  good: { label: 'Good', color: '#17a2b8' },
  fair: { label: 'Fair', color: '#ffc107' },
  poor: { label: 'Poor', color: '#dc3545' },
};

const Marketplace: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState<'newest' | 'price_low' | 'price_high' | 'popular'>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    loadListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadListings uses selectedCategory, sortBy from closure
  }, [selectedCategory, sortBy]);

  const loadListings = async () => {
    try {
      setLoading(true);
      setError(null);
      const cat = selectedCategory !== 'All Categories' ? selectedCategory : undefined;
      const [productsRes, personalRes] = await Promise.all([
        productsPublicApi.list({
          search: searchQuery || undefined,
          category: cat,
          page: 1,
          limit: 50,
        }),
        personalMarketplaceApi.listPublic({
          limit: 50,
          category: cat,
          search: searchQuery || undefined,
        }),
      ]);
      const rawProducts = productsRes?.products ?? productsRes?.data ?? [];
      const productMapped: MarketplaceListing[] = (Array.isArray(rawProducts) ? rawProducts : []).map((p: any) => {
        const variant = p.variants?.[0];
        const price = variant?.price ?? p.price ?? 0;
        const img = p.primaryImage ?? p.imageUrl ?? '';
        return {
          id: `p-${p.id}`,
          sellerId: p.business?.id ?? p.businessId ?? '',
          sellerName: p.business?.name ?? '',
          sellerMangoId: p.business?.mangoId ?? '',
          sellerRating: 0,
          title: p.name ?? '',
          description: p.description ?? p.shortDescription ?? '',
          price: typeof price === 'number' ? price : parseFloat(String(price)) || 0,
          acceptMangoCredits: false,
          images: img ? [img] : [],
          category: p.category ?? p.productType ?? 'Other',
          condition: 'new',
          location: '',
          status: 'active',
          views: 0,
          saves: 0,
          createdAt: p.createdAt ?? new Date().toISOString(),
          expiresAt: undefined,
        };
      });
      const personalList = personalRes?.success && Array.isArray(personalRes?.data) ? personalRes.data : [];
      const personalMapped: MarketplaceListing[] = personalList.map((p: any) => ({
        id: `m-${p.id}`,
        sellerId: p.sellerId ?? '',
        sellerName: p.sellerName ?? '',
        sellerMangoId: p.sellerMangoId ?? '',
        sellerRating: 0,
        title: p.title ?? '',
        description: p.description ?? '',
        price: typeof p.price === 'number' ? p.price : parseFloat(String(p.price)) || 0,
        acceptMangoCredits: p.acceptMangoCredits ?? false,
        images: Array.isArray(p.images) ? p.images : [],
        category: p.category ?? 'Other',
        condition: p.condition ?? 'good',
        location: p.location ?? '',
        status: p.status ?? 'active',
        views: p.views ?? 0,
        saves: p.saves ?? 0,
        createdAt: p.createdAt ?? new Date().toISOString(),
        expiresAt: undefined,
      }));
      setListings([...personalMapped, ...productMapped]);
    } catch (err) {
      console.error('Failed to load listings:', err);
      setError('Failed to load listings. Please try again.');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadListings();
  };

  const handleSaveListing = async (listing: MarketplaceListing) => {
    try {
      const res = await marketplaceApi.saveListing({
        id: listing.id,
        title: listing.title,
        price: listing.price,
        imageUrl: listing.images[0],
      });
      if (res?.success) setSavedIds((prev) => new Set(prev).add(listing.id));
    } catch (err) {
      console.error('Failed to save listing:', err);
    }
  };

  // NOTE: Demo data removed - using live API data

  // Filter and sort listings
  let filteredListings = [...listings];
  
  if (searchQuery) {
    filteredListings = filteredListings.filter(l => 
      l.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  
  if (selectedCategory !== 'All Categories') {
    filteredListings = filteredListings.filter(l => l.category === selectedCategory);
  }
  
  if (priceRange.min) {
    filteredListings = filteredListings.filter(l => l.price >= parseFloat(priceRange.min));
  }
  if (priceRange.max) {
    filteredListings = filteredListings.filter(l => l.price <= parseFloat(priceRange.max));
  }
  
  switch (sortBy) {
    case 'newest':
      filteredListings.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      break;
    case 'price_low':
      filteredListings.sort((a, b) => a.price - b.price);
      break;
    case 'price_high':
      filteredListings.sort((a, b) => b.price - a.price);
      break;
    case 'popular':
      filteredListings.sort((a, b) => b.views - a.views);
      break;
  }

  const currentPlan = user ? SUBSCRIPTION_PLANS[user.subscription] : SUBSCRIPTION_PLANS.free;
  const canCreateListings = currentPlan.limits.productListings > 0;

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '25px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🛒 Marketplace</h1>
          <p style={{ margin: '10px 0 0 0', color: '#666' }}>
            Buy and sell with other Mango users
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => navigate('/marketplace/my-listings')}
            style={{
              padding: '12px 20px',
              background: 'white',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📋 My Listings
          </button>
          <button
            onClick={() => navigate('/marketplace/create')}
            disabled={!canCreateListings}
            style={{
              padding: '12px 24px',
              background: canCreateListings 
                ? 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)'
                : '#e0e0e0',
              color: canCreateListings ? 'white' : '#666',
              border: 'none',
              borderRadius: '10px',
              cursor: canCreateListings ? 'pointer' : 'not-allowed',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            + List an Item
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '25px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ flex: '1 1 300px', display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search listings..."
              style={{
                flex: 1,
                padding: '12px 16px',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '14px'
              }}
            />
            <button
              onClick={handleSearch}
              style={{
                padding: '12px 20px',
                background: '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Search
            </button>
          </div>
          
          {/* Category */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              fontSize: '14px',
              minWidth: '180px',
              cursor: 'pointer'
            }}
          >
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          
          {/* Price Range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="number"
              value={priceRange.min}
              onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
              placeholder="Min $"
              style={{
                width: '100px',
                padding: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '14px'
              }}
            />
            <span style={{ color: '#999' }}>-</span>
            <input
              type="number"
              value={priceRange.max}
              onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
              placeholder="Max $"
              style={{
                width: '100px',
                padding: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '14px'
              }}
            />
          </div>
          
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            style={{
              padding: '12px 16px',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="popular">Most Popular</option>
          </select>
          
          {/* View Mode */}
          <div style={{ display: 'flex', gap: '5px' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '12px',
                background: viewMode === 'grid' ? '#ff6b35' : '#f0f0f0',
                color: viewMode === 'grid' ? 'white' : '#666',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              ⊞
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '12px',
                background: viewMode === 'list' ? '#ff6b35' : '#f0f0f0',
                color: viewMode === 'list' ? 'white' : '#666',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              ☰
            </button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          background: '#fee',
          color: '#c00',
          padding: '12px 16px',
          borderRadius: '10px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Results count */}
      <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>
        Showing {filteredListings.length} {filteredListings.length === 1 ? 'listing' : 'listings'}
      </p>

      {/* Listings Grid/List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <span style={{ fontSize: '48px' }}>🔍</span>
          <p style={{ color: '#666', marginTop: '15px' }}>Loading marketplace...</p>
        </div>
      ) : filteredListings.length === 0 ? (
        <div style={{ 
          background: 'white', 
          padding: '60px', 
          borderRadius: '16px', 
          textAlign: 'center' 
        }}>
          <span style={{ fontSize: '64px' }}>🔍</span>
          <h2 style={{ margin: '20px 0 10px 0', color: '#333' }}>No listings found</h2>
          <p style={{ color: '#666', margin: 0 }}>
            Try adjusting your search or filters
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px'
        }}>
          {filteredListings.map(listing => (
            <div
              key={listing.id}
              style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
              }}
            >
              {/* Image */}
              <div style={{
                height: '200px',
                background: `url(${listing.images[0]}) center/cover, #f0f0f0`,
                position: 'relative'
              }}>
                {listing.acceptMangoCredits && (
                  <span style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    background: 'rgba(255,107,53,0.9)',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    🥭 Accepts Credits
                  </span>
                )}
                <button type="button" onClick={(e) => { e.stopPropagation(); handleSaveListing(listing); }} title={savedIds.has(listing.id) ? 'Saved to your wishlist' : 'Save listing'} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.4)', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' }}>{savedIds.has(listing.id) ? '✓' : '🔖'}</button>
                <span style={{
                  position: 'absolute',
                  top: '10px',
                  right: '50px',
                  background: CONDITIONS[listing.condition].color,
                  color: 'white',
                  padding: '5px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {CONDITIONS[listing.condition].label}
                </span>
              </div>
              
              {/* Content */}
              <div style={{ padding: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '16px', 
                    color: '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}>
                    {listing.title}
                  </h3>
                  <button type="button" onClick={(e) => { e.stopPropagation(); handleSaveListing(listing); }} title={savedIds.has(listing.id) ? 'Saved to your wishlist' : 'Save'} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', fontSize: '16px' }}>{savedIds.has(listing.id) ? '✓' : '🔖'}</button>
                </div>
                <p style={{ 
                  margin: '8px 0 0 0', 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  color: '#ff6b35' 
                }}>
                  ${listing.price.toLocaleString()}
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#888' }}>
                  📍 {listing.location}
                </p>
                
                {/* Seller & Stats */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '15px',
                  paddingTop: '15px',
                  borderTop: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: '#667eea',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {listing.sellerName.charAt(0)}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#333' }}>{listing.sellerName}</p>
                      <p style={{ margin: 0, fontSize: '10px', color: '#888' }}>⭐ {listing.sellerRating}</p>
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#888' }}>
                    👁 {listing.views} • ❤️ {listing.saves}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {filteredListings.map(listing => (
            <div
              key={listing.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                display: 'flex',
                gap: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '180px',
                height: '140px',
                borderRadius: '10px',
                background: `url(${listing.images[0]}) center/cover, #f0f0f0`,
                flexShrink: 0
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>{listing.title}</h3>
                    <p style={{ margin: '10px 0 0 0', fontSize: '28px', fontWeight: '700', color: '#ff6b35' }}>
                      ${listing.price.toLocaleString()}
                    </p>
                  </div>
                  <span style={{
                    background: CONDITIONS[listing.condition].color,
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {CONDITIONS[listing.condition].label}
                  </span>
                </div>
                <p style={{ 
                  margin: '12px 0 0 0', 
                  fontSize: '14px', 
                  color: '#666',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {listing.description}
                </p>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '15px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '13px', color: '#888' }}>
                    <span>📍 {listing.location}</span>
                    <span>👁 {listing.views} views</span>
                    {listing.acceptMangoCredits && <span>🥭 Accepts Mango Credits</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#333' }}>{listing.sellerName}</span>
                    <span style={{ fontSize: '12px', color: '#888' }}>⭐ {listing.sellerRating}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Marketplace;
